import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';

import { theme } from './theme';
import { APP_QUERY_DEFAULTS } from './lib/query-client';
import { RequireAdmin } from './routes/RequireAdmin';
import { Login } from './routes/Login';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Users } from './routes/Users';
import { Audit } from './routes/Audit';
import { AccessFailures } from './routes/AccessFailures';
import { UserDetail } from './routes/UserDetail';
import { Feedback } from './routes/Feedback';

// Better Stack is log ingestion, not browser error tracking. A stack trace from a
// colleague's browser is the thing that will actually be needed.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({ dsn: import.meta.env.VITE_SENTRY_DSN, tracesSampleRate: 0 });
}

const queryClient = new QueryClient({ defaultOptions: { queries: APP_QUERY_DEFAULTS } });

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Notifications />
          {/* Last-resort catch-all. The one that matters is inside AppShell,
              around <Outlet />, so a broken data screen doesn't blank the
              header/nav/sign-out along with it. */}
          <ErrorBoundary label="Admin">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<RequireAdmin />}>
                <Route
                  path="/users"
                  element={
                    <ErrorBoundary label="Users">
                      <Users />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/users/:id"
                  element={
                    <ErrorBoundary label="Journey">
                      <UserDetail />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/feedback"
                  element={
                    <ErrorBoundary label="Feedback">
                      <Feedback />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/audit"
                  element={
                    <ErrorBoundary label="Audit">
                      <Audit />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/access-failures"
                  element={
                    <ErrorBoundary label="Access problems">
                      <AccessFailures />
                    </ErrorBoundary>
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to="/users" replace />} />
            </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);
